import { SUITS, RANKS } from "@/lib/deck";
import { cn } from "@/lib/utils";

function StatBox({ label, value, testId, accent }) {
  return (
    <div
      className="border-2 border-foreground bg-card p-4"
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
        {label}
      </div>
      <div
        className={cn(
          "text-4xl lg:text-5xl font-extrabold font-mono tabular-nums tracking-tighter leading-none",
          accent
        )}
      >
        {value}
      </div>
    </div>
  );
}

export function StatsPanel({ stats, showCount }) {
  const { totalRemaining, totalDealt, bySuit, byRank, runningCount, trueCount } = stats;

  return (
    <div className="space-y-4" data-testid="stats-panel">
      <div className="grid grid-cols-2 gap-3">
        <StatBox
          label="Remaining"
          value={totalRemaining}
          testId="stat-total-remaining"
        />
        <StatBox
          label="Dealt"
          value={totalDealt}
          testId="stat-total-dealt"
        />
      </div>

      {showCount && (
        <div className="grid grid-cols-2 gap-3" data-testid="count-section">
          <StatBox
            label="Running"
            value={runningCount > 0 ? `+${runningCount}` : runningCount}
            testId="stat-running-count"
            accent={runningCount > 0 ? "text-suit-red" : ""}
          />
          <StatBox
            label="True Count"
            value={trueCount > 0 ? `+${trueCount.toFixed(1)}` : trueCount.toFixed(1)}
            testId="stat-true-count"
            accent={trueCount > 0 ? "text-suit-red" : ""}
          />
        </div>
      )}

      {/* Remaining by suit */}
      <div className="border-2 border-foreground bg-card" data-testid="stat-by-suit">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground px-4 pt-4 pb-2">
          Remaining by Suit
        </div>
        <div className="grid grid-cols-4 divide-x-2 divide-foreground border-t-2 border-foreground">
          {SUITS.map((suit) => (
            <div
              key={suit.id}
              className="flex flex-col items-center gap-1 py-3"
              data-testid={`stat-suit-${suit.id}`}
            >
              <span
                className={cn(
                  "text-lg leading-none",
                  suit.color === "red" ? "text-suit-red" : "text-suit-black"
                )}
                aria-hidden="true"
              >
                {suit.symbol}
              </span>
              <span className="text-xl font-bold tabular-nums">{bySuit[suit.id]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Remaining by rank */}
      <div className="border-2 border-foreground bg-card" data-testid="stat-by-rank">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground px-4 pt-4 pb-2">
          Remaining by Rank
        </div>
        <div className="grid grid-cols-7 border-t-2 border-l-2 border-foreground">
          {RANKS.map((rank) => (
            <div
              key={rank}
              className="flex flex-col items-center gap-0.5 py-2 border-r-2 border-b-2 border-foreground"
              data-testid={`stat-rank-${rank}`}
            >
              <span className="text-[0.65rem] uppercase text-muted-foreground">{rank}</span>
              <span className="text-base font-bold tabular-nums">{byRank[rank]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
